import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {HttpService} from '@core/services';
import {ApiCollection, ApiResponse, DashboardNotification} from '@core/types';
import {ENDPOINTS, GET_URL, log} from '@core/utils';
import {CompanyStateService} from '@core/states';
import {AuthStateService} from '@auth/states';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly cacheTtlMs = 15000;
  private readonly listCache = new Map<string, {at: number; result: DashboardNotification[]}>();
  private readonly inFlight = new Map<string, Promise<ApiResponse<DashboardNotification[]>>>();

  constructor(
    private apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async getNotifications(page = 1, itemsPerPage = 20, options?: {skipLoader?: boolean}): Promise<ApiResponse<DashboardNotification[]>> {
    const cacheKey = this.buildCacheKey(page, itemsPerPage);
    const cached = this.listCache.get(cacheKey);
    if (cached && (Date.now() - cached.at) < this.cacheTtlMs) {
      return {success: true, result: cached.result};
    }

    const pending = this.inFlight.get(cacheKey);
    if (pending) {
      return pending;
    }

    const requestPromise = this.fetchNotifications(page, itemsPerPage, options)
      .finally(() => {
        this.inFlight.delete(cacheKey);
      });
    this.inFlight.set(cacheKey, requestPromise);
    const response = await requestPromise;
    if (response.success && response.result) {
      this.listCache.set(cacheKey, {at: Date.now(), result: response.result});
    }
    return response;
  }

  private async fetchNotifications(page = 1, itemsPerPage = 20, options?: {skipLoader?: boolean}): Promise<ApiResponse<DashboardNotification[]>> {
    try {
      const url = GET_URL(ENDPOINTS.NOTIFICATIONS, {page, itemsPerPage});
      let headers = this.buildHeaders() ?? new HttpHeaders();
      if (options?.skipLoader) {
        headers = headers.set('X-Skip-Loader', '1');
      }
      const response: ApiCollection<DashboardNotification> = await this.apiService.get(url, headers ? {headers} : undefined);
      const notifications = this.apiService.normalizeCollection<any>(response).member.map(item => ({
        ...item,
        name: item.name ?? item.title,
        description: item.description,
        isRead: item.isRead ?? !!item.readAt
      })) as DashboardNotification[];
      return {success: true, result: notifications};
    } catch (error: any) {
      log.error('Error loading notifications:', error);
    }
    return {
      success: false,
      message: 'No se pudieron cargar las notificaciones.'
    };
  }

  private buildCacheKey(page: number, itemsPerPage: number): string {
    const companyId = this.companyState.membership()?.company?.id ?? 'admin';
    return `${companyId}:${page}:${itemsPerPage}`;
  }

  async markAsRead(notificationId: string | number): Promise<ApiResponse<DashboardNotification>> {
    try {
      const url = GET_URL(ENDPOINTS.NOTIFICATION_READ, {id: notificationId});
      let headers = this.buildHeaders() ?? new HttpHeaders();
      headers = headers.set('X-Skip-Loader', '1');
      const response: DashboardNotification = await this.apiService.patch(url, {}, headers ? {headers} : undefined);
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error marking notification as read:', error);
    }
    return {
      success: false,
      message: 'No se pudo marcar como leida la notificacion.'
    };
  }

  async executeAction(notificationId: string | number, actionSlug: string): Promise<ApiResponse<DashboardNotification>> {
    try {
      const url = GET_URL(ENDPOINTS.NOTIFICATION_ACTION, {id: notificationId}) + '/execute';
      const headers = this.buildHeaders();
      const response: DashboardNotification = await this.apiService.post(url, {action: actionSlug}, headers ? {headers} : undefined);
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error executing notification action:', error);
    }
    return {
      success: false,
      message: 'No se pudo ejecutar la accion.'
    };
  }

  private buildHeaders(): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      return null;
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      return null;
    }

    return new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
  }
}
